import { Heading, List, Stack, Text, Image } from "@chakra-ui/react";
import { ReactNode } from "react";

import CustomWrapper from "components/Chakra/CustomWrapper";
import Button from "components/Chakra/Button";
import {
  PricePackageType,
  usePricePackageMatterportModal,
} from "modals/usePricePackageMatterportModal";
import IconCamera from "components/Chakra/Icons/Camera";
import IconCam from "components/Chakra/Icons/Cam";
import IconArea from "components/Chakra/Icons/Area";
import IconRuler from "components/Chakra/Icons/Ruler";
import IconStopwatch from "components/Chakra/Icons/Stopwatch";

type CardProps = {
  type: PricePackageType;
  title: string;
  subTitle?: string;
  price?: string;
  img: string;
  descriptions?: Array<{ icon?: ReactNode; text: string }>;
  notes?: Array<string>;
  textButton: string;
};

const cards: Array<CardProps> = [
  {
    type: "BASIC",
    title: "Základní",
    subTitle: "Vhodný pro nabídky pronájmů",
    price: "4 000 Kč",
    img: "/img/scenes/Matterport/house.png",
    descriptions: [
      { icon: <IconCamera fill="ud-primary" />, text: "Profesionální foto" },
      { icon: <IconCam fill="ud-primary" />, text: "3D scan" },
      { icon: <IconRuler fill="ud-primary" />, text: "Půdorys" },
      { icon: <IconArea fill="ud-primary" />, text: "Do 100 m2" },
      {
        icon: (
          <IconStopwatch
            fill="ud-primary"
            sx={{
              "*": {
                fill: "red",
              },
            }}
          />
        ),
        text: "Doba realizace: 1,5 h",
      },
    ],
    textButton: "Chci základní balíček",
  },
  {
    type: "ADVANCED",
    title: "Rozšířený",
    subTitle: "Vhodný pro nabídky prodejů",
    price: "10 000 Kč",
    img: "/img/scenes/Matterport/building.png",
    descriptions: [
      { icon: <IconCamera fill="ud-primary" />, text: "Profesionální foto" },
      { icon: <IconCam fill="ud-primary" />, text: "3D scan" },
      { icon: <IconCam fill="ud-primary" />, text: "Videoprohlídka s dronem" },
      { icon: <IconRuler fill="ud-primary" />, text: "Půdorys" },
      { icon: <IconArea fill="ud-primary" />, text: "Do 200 m2" },
      {
        icon: <IconStopwatch fill="ud-primary" />,
        text: "Doba realizace: 1,5 h",
      },
    ],
    textButton: "Chci rozšířený balíček",
  },
  {
    type: "CUSTOM",
    title: "Na míru",
    subTitle: "---",
    price: "---",
    img: "/img/scenes/Matterport/custom-img.png",
    notes: [
      "Vhodné pro atypické prostory jako jsou historické objekty, pozemky nebo jen pro potřebu jedné části.",
      "Napiště nám vaši představu a my vám obratem pošleme nacenění",
    ],
    textButton: "Chci balíček na míru",
  },
];

const Card = ({
  title,
  subTitle,
  price,
  descriptions,
  textButton,
  notes,
  type,
  img,
}: CardProps) => {
  const { openModal } = usePricePackageMatterportModal();

  return (
    <Stack
      flex={1}
      maxW={{ base: "100%", md: "440px" }}
      p={{ base: "24px 16px", md: "40px" }}
      mt={{ lg: type === "ADVANCED" ? "-20px !important" : "unset" }}
      mb={{ lg: type === "ADVANCED" ? "-20px !important" : "unset" }}
      borderRadius="16px"
      backgroundColor="ud-grey-100"
      align="center"
      spacing={0}
      sx={{
        "& > *": {
          width: "100%",
          textAlign: "center",
        },
      }}
    >
      <Stack align="center">
        <Image src={img} alt={title} boxSize="48px" />
      </Stack>
      <Heading
        pt="12px"
        pb={{ base: type === "CUSTOM" ? "24px" : "unset", md: "unset" }}
      >
        {title}
      </Heading>
      <Text
        display={{ base: type === "CUSTOM" ? "none" : "block", md: "unset" }}
        visibility={type !== "CUSTOM" ? "visible" : "hidden"}
      >
        {subTitle}
      </Text>
      <Heading
        as="h3"
        display={{ base: type === "CUSTOM" ? "none" : "block", md: "unset" }}
        visibility={type !== "CUSTOM" ? "visible" : "hidden"}
        variant="h1"
        p={{ base: "12px 0 24px", md: "12px 0 40px" }}
        fontWeight="bold"
        bgGradient="linear-gradient(86deg, #E74C3C 0%, #E98540 100%)"
        bgClip="text"
      >
        {price}
      </Heading>
      {descriptions && descriptions.length > 0 && (
        <List
          display="flex"
          flexDirection="column"
          pt={{ base: "24px", md: "40px" }}
          pb={{ base: "40px", md: "64px" }}
          px={{ base: "8px", lg: "32px" }}
          flex={1}
          borderTop="1px solid"
          spacing={0}
          gap={{ base: "16px", md: "24px" }}
          borderColor="ud-grey-300"
        >
          {descriptions.map((description, index) => (
            <Stack key={index} direction="row" align="center">
              {description.icon}
              <Text textAlign="left" fontWeight="bold">
                {description.text}
              </Text>
            </Stack>
          ))}
        </List>
      )}
      {notes && notes?.length > 0 && (
        <Stack spacing={1} flex={1}>
          {notes.map((note, index) => (
            <Text
              _first={{
                borderTop: "1px solid",
                borderColor: "ud-grey-300",
                pt: { base: "24px", md: "40px" },
                fontWeight: "bold",
              }}
              _notFirst={{
                pb: { base: "40px", md: "64px" },
              }}
              px={{ lg: "32px" }}
              textAlign="center"
              key={index}
            >
              {note}
            </Text>
          ))}
        </Stack>
      )}
      <Button
        borderRadius="full"
        variant={type !== "ADVANCED" ? "outline" : "gradient"}
        backgroundColor={type !== "ADVANCED" ? "inherit" : "unset"}
        fontSize="16px"
        w="fit-content !important"
        ml="1"
        px={{ base: "24px", md: "32px" }}
        onClick={() => openModal({ type, title })}
      >
        {textButton}
      </Button>
    </Stack>
  );
};

const PackageCards = () => (
  <CustomWrapper
    maxW="1360px"
    contentProps={{
      m: { base: "80px 0", md: "80px 0 128px" },
    }}
  >
    <Heading textAlign="center" pb={{ base: "40px", md: "40px", lg: "60px" }}>
      Vyberte si z balíčků
    </Heading>
    <Stack
      direction={{ base: "column", md: "row" }}
      justify="center"
      flexWrap="wrap"
      spacing={0}
      gap={{ base: "32px", md: "20px" }}
    >
      {cards.map((card, index) => (
        <Card key={index} {...card} />
      ))}
    </Stack>
  </CustomWrapper>
);

export default PackageCards;
